import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { ITerminal } from "../../../../../Patio/Cadastro/Terminal/Create/types/types";
import { IProprietarioCargas } from "../../../../../Patio/Cadastro/ProprietarioCargas/Create/types/types";
import { ITransportadoras } from "../../../../../Patio/Cadastro/Transportadoras/types/types";
import { IOperacoesPortoCarrossel } from "../../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IOperacoesPortoCarrossel;
  onConfirm: () => void;
  onClose: () => void;
}

interface FormValues {
  id_terminal: string;
  id_proprietario_carga: string;
  id_transportadora: string;
  data_inicio_operacao: string;
  data_fim_operacao: string;
  urgencia: boolean;
  intervalo_acesso_liberado_porto: string;
  valor_tonelada: string;
  tonelada_prevista_transportadas: string;
  toneladas_transportadas: string;
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [terminais, setTerminal] = useState([]);
  const [proprietarios, setProprietarios] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const [selectedTransportadora, setSelectedTransportadora] = useState<any>(null);
  const { user } = useAuth();

  const handleSubmit = useCallback(
    async (values: FormValues, row?: IOperacoesPortoCarrossel) => {
      try {
        setLoading(true);

        const userId = user?.id;

        let status = 0;
        let dataFim = null;

        if (values.data_fim_operacao) {
          dataFim = format(
            new Date(values.data_fim_operacao),
            "yyyy-MM-dd HH:mm:ss",
            { locale: ptBR }
          );
          status = 3;
        }

        let body: any = {
          id_terminal: Number(values.id_terminal),
          id_proprietario_carga: Number(values.id_proprietario_carga),
          id_transportadora: Number(values.id_transportadora),
          data_inicio_operacao: format(
            new Date(values.data_inicio_operacao),
            "yyyy-MM-dd HH:mm:ss",
            { locale: ptBR }
          ),
          data_fim_operacao: dataFim,
          urgencia: values.urgencia,
          intervalo_acesso_liberado_porto: Number(values.intervalo_acesso_liberado_porto),
          valor_tonelada: Number(
            String(values.valor_tonelada).replace(",", ".")
          ),
          tonelada_prevista_transportadas: Number(values.tonelada_prevista_transportadas),
          toneladas_transportadas: Number(values.toneladas_transportadas),
          ativo: true,
          id_usuario_historico: userId,
          status: status,
        };

        if (row) {
          body = {
            ...body,
            id_operacao_porto_carrossel: row.id_operacao_porto_carrossel,
          };
        }

        const endpoint = props.isEdit
          ? "/editar/operacaoPortoCarrossel"
          : "/cadastrar/operacaoPortoCarrossel";

        const response = await api.post(endpoint, body);

        if (response.status === 200) {
          props.onConfirm();
        } else {
          FrontendNotification("Erro ao salvar a operação!", "error");
        }

        setLoading(false);
      } catch {
        setLoading(false);
        FrontendNotification("Erro ao salvar a operação!", "error");
      }
    },
    [props.isEdit]
  );

  const getTerminais = useCallback(async () => {
    try {
      setLoading(true);
      const body = {
        order_by: "data_historico",
        order_direction: "desc",
        qtd_por_pagina: 100,
      };
      const response = await api.post("/listar/terminais", body);
      if (response.status === 200) {
        const mappingResponse = response.data.data.map((item: ITerminal) => ({
          label: item.razao_social,
          value: item.id_terminal,
        }));
        setTerminal(mappingResponse);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  const getProprietarios = useCallback(async () => {
    try {
      setLoading(true);
      const body = {
        order_by: "data_historico",
        order_direction: "desc",
        qtd_por_pagina: 100,
      };
      const response = await api.post("/listar/proprietariosCarga", body);
      if (response.status === 200) {
        const mappingResponse = response.data.data.map((item: IProprietarioCargas) => ({
          label: item.razao_social,
          value: item.id_proprietario_carga,
        }));
        setProprietarios(mappingResponse);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  const getTransportadoras = useCallback(async (searchTerm: string = "") => {
    try {
      const body = {
        order_by: "razao_social",
        order_direction: "asc",
        qtd_por_pagina: 100,
        ...(searchTerm.trim().length > 0
          ? { razao_social: searchTerm.trim() }
          : {}),
      };
      const response = await api.post("/listar/transportadoras", body);
      if (response.status === 200) {
        return response.data.data.map((item: ITransportadoras) => ({
          label: item.razao_social,
          value: item.id_transportadora,
        }));
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const onLoadFormValues = useCallback((row?: IOperacoesPortoCarrossel) => {
    if (row) {
      formik.setFieldValue("id_terminal", row.id_terminal);
      formik.setFieldValue("id_proprietario_carga", row.id_proprietario_carga);
      const vinculoTransportadora = (row as any).operacaoPortoCarrosselTransportadora;
      if (vinculoTransportadora?.transportadora) {
        setSelectedTransportadora({
          label: vinculoTransportadora.transportadora.razao_social,
          value: vinculoTransportadora.id_transportadora,
        });
        formik.setFieldValue(
          "id_transportadora",
          vinculoTransportadora.id_transportadora
        );
      } else {
        formik.setFieldValue("id_transportadora", row.id_transportadora);
      }
      formik.setFieldValue("data_inicio_operacao", row.data_inicio_operacao);
      formik.setFieldValue("data_fim_operacao", row.data_fim_operacao);
      formik.setFieldValue("urgencia", row.urgencia);
      formik.setFieldValue("intervalo_acesso_liberado_porto", row.intervalo_acesso_liberado_porto);
      formik.setFieldValue("valor_tonelada", row.valor_tonelada);
      formik.setFieldValue("tonelada_prevista_transportadas", row.tonelada_prevista_transportadas);
      formik.setFieldValue("toneladas_transportadas", row.toneladas_transportadas);
    }
  }, []);

  const initialValues: FormValues = {
    id_terminal: "",
    id_proprietario_carga: "",
    id_transportadora: "",
    data_inicio_operacao: "",
    data_fim_operacao: "",
    urgencia: false,
    intervalo_acesso_liberado_porto: "",
    valor_tonelada: "",
    tonelada_prevista_transportadas: "",
    toneladas_transportadas: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) => {
      handleSubmit(values, props.selectedRow);
    },
  });

  useEffect(() => {
    getTerminais();
    getProprietarios();
    getTransportadoras().then((options: any) => setTransportadoras(options));
  }, [getTerminais, getProprietarios, getTransportadoras]);

  useEffect(() => {
    if (props.isView || props.isEdit) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.isView, props.isEdit, props.selectedRow, onLoadFormValues]);

  return (
    <>
      <Loading loading={loading} skeletonFields={14} skeletonColumns={3} />
      <ToastContainer />
      <div className="overflow-y-scroll max-h-[calc(70vh)] p-5">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <SelectCustom
              id="id_terminal"
              data={terminais}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("id_terminal", selectedOption.value);
              }}
              title="Terminal"
              touched={formik.touched.id_terminal}
              error={formik.errors.id_terminal}
              disabled={props.isView}
              value={formik.values.id_terminal}
            />
          </div>
          <div>
            <SelectCustom
              id="id_proprietario_carga"
              data={proprietarios}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("id_proprietario_carga", selectedOption.value);
              }}
              title="Proprietário de Carga"
              touched={formik.touched.id_proprietario_carga}
              error={formik.errors.id_proprietario_carga}
              disabled={props.isView}
              value={formik.values.id_proprietario_carga}
            />
          </div>
          <div>
            <SelectCustom
              id="id_transportadora"
              async
              data={getTransportadoras}
              defaultOptions={transportadoras}
              defaultValue={selectedTransportadora}
              onChange={(selectedOption: any) => {
                setSelectedTransportadora(selectedOption);
                formik.setFieldValue(
                  "id_transportadora",
                  selectedOption?.value || ""
                );
              }}
              title="Transportadora"
              touched={formik.touched.id_transportadora}
              error={formik.errors.id_transportadora}
              disabled={props.isView}
              placeholder="Digite para buscar a transportadora"
            />
          </div>
        </div>

        {!props.isEdit ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <InputCustom
                  id="data_inicio_operacao"
                  title="Data Início"
                  type="datetime-local"
                  placeholder=""
                  onChange={formik.handleChange("data_inicio_operacao")}
                  value={formik.values.data_inicio_operacao}
                  touched={formik.touched.data_inicio_operacao}
                  error={formik.errors.data_inicio_operacao}
                  disabled={props.isView}
                />
              </div>
              <div>
                <InputCustom
                  id="intervalo_acesso_liberado_porto"
                  title="Intervalo Acesso (min)"
                  type="number"
                  placeholder=""
                  onChange={formik.handleChange("intervalo_acesso_liberado_porto")}
                  value={formik.values.intervalo_acesso_liberado_porto}
                  touched={formik.touched.intervalo_acesso_liberado_porto}
                  error={formik.errors.intervalo_acesso_liberado_porto}
                  disabled={props.isView}
                />
              </div>
              <div>
                <InputCustom
                  id="valor_tonelada"
                  title="Valor Tonelada"
                  type="number"
                  step="0.01"
                  placeholder=""
                  onChange={formik.handleChange("valor_tonelada")}
                  value={formik.values.valor_tonelada}
                  touched={formik.touched.valor_tonelada}
                  error={formik.errors.valor_tonelada}
                  disabled={props.isView}
                />
              </div>
            </div>

            <div className="grid grid-cols-[1.2fr_1.2fr_0.6fr] gap-3 mb-4">
              <div>
                <InputCustom
                  id="tonelada_prevista_transportadas"
                  title="Tonelada Prevista"
                  type="number"
                  placeholder=""
                  onChange={formik.handleChange("tonelada_prevista_transportadas")}
                  value={formik.values.tonelada_prevista_transportadas}
                  touched={formik.touched.tonelada_prevista_transportadas}
                  error={formik.errors.tonelada_prevista_transportadas}
                  disabled={props.isView}
                />
              </div>
              <div>
                <InputCustom
                  id="toneladas_transportadas"
                  title="Toneladas Transportadas"
                  type="number"
                  placeholder=""
                  onChange={formik.handleChange("toneladas_transportadas")}
                  value={formik.values.toneladas_transportadas}
                  touched={formik.touched.toneladas_transportadas}
                  error={formik.errors.toneladas_transportadas}
                  disabled={props.isView}
                  styleTitle={{ whiteSpace: "nowrap" }}
                />
              </div>
              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  id="urgencia"
                  name="urgencia"
                  checked={formik.values.urgencia}
                  onChange={formik.handleChange}
                  disabled={props.isView}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="urgencia"
                  className="ml-2 text-sm font-medium text-gray-900"
                >
                  Urgência
                </label>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <InputCustom
                  id="data_inicio_operacao"
                  title="Data Início"
                  type="datetime-local"
                  placeholder=""
                  onChange={formik.handleChange("data_inicio_operacao")}
                  value={formik.values.data_inicio_operacao}
                  touched={formik.touched.data_inicio_operacao}
                  error={formik.errors.data_inicio_operacao}
                  disabled={props.isView}
                />
              </div>
              <div>
                <InputCustom
                  id="data_fim_operacao"
                  title="Data Fim"
                  type="datetime-local"
                  placeholder=""
                  onChange={formik.handleChange("data_fim_operacao")}
                  value={formik.values.data_fim_operacao}
                  touched={formik.touched.data_fim_operacao}
                  error={formik.errors.data_fim_operacao}
                  disabled={props.isView}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <InputCustom
                  id="intervalo_acesso_liberado_porto"
                  title="Intervalo Acesso (min)"
                  type="number"
                  placeholder=""
                  onChange={formik.handleChange(
                    "intervalo_acesso_liberado_porto"
                  )}
                  value={formik.values.intervalo_acesso_liberado_porto}
                  touched={formik.touched.intervalo_acesso_liberado_porto}
                  error={formik.errors.intervalo_acesso_liberado_porto}
                  disabled={props.isView}
                />
              </div>
              <div>
                <InputCustom
                  id="valor_tonelada"
                  title="Valor Tonelada"
                  type="number"
                  step="0.01"
                  placeholder=""
                  onChange={formik.handleChange("valor_tonelada")}
                  value={formik.values.valor_tonelada}
                  touched={formik.touched.valor_tonelada}
                  error={formik.errors.valor_tonelada}
                  disabled={props.isView}
                />
              </div>
              <div>
                <InputCustom
                  id="tonelada_prevista_transportadas"
                  title="Tonelada Prevista"
                  type="number"
                  placeholder=""
                  onChange={formik.handleChange(
                    "tonelada_prevista_transportadas"
                  )}
                  value={formik.values.tonelada_prevista_transportadas}
                  touched={formik.touched.tonelada_prevista_transportadas}
                  error={formik.errors.tonelada_prevista_transportadas}
                  disabled={props.isView}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <InputCustom
                  id="toneladas_transportadas"
                  title="Toneladas Transportadas"
                  type="number"
                  placeholder=""
                  onChange={formik.handleChange("toneladas_transportadas")}
                  value={formik.values.toneladas_transportadas}
                  touched={formik.touched.toneladas_transportadas}
                  error={formik.errors.toneladas_transportadas}
                  disabled={props.isView}
                />
              </div>
              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  id="urgencia"
                  name="urgencia"
                  checked={formik.values.urgencia}
                  onChange={formik.handleChange}
                  disabled={props.isView}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="urgencia"
                  className="ml-2 text-sm font-medium text-gray-900"
                >
                  Urgência
                </label>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="sticky bottom-0 w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
          onClick={() => formik.handleSubmit()}
          id="btnSave"
        >
          Salvar
        </button>
      </div>
    </>
  );
};

export default Form;
